// Grids & Gantts — the structure, exported.
//
// THE DASHBOARD EXPORTERS CANNOT DO THIS PAGE, and it is worth saying why
// rather than discovering it in a meeting. They work by reading the DOM:
// the workbook is built from the tables inside each `[data-pdf="card"]`,
// and the PDF is a photograph of those cards. This grid is WINDOWED — at
// any moment about thirty of its rows exist in the browser. A photograph
// would catch those thirty, and a DOM-read workbook would catch whatever
// happened to be scrolled into view.
//
// So both exports here are built from the ROWS THEMSELVES, through the same
// `cellOf` the grid draws with. What comes out is what is on screen —
// every row of it, open or shut as the reader left it, in the columns the
// reader has, with the indentation that makes a structure readable.
//
// Generator rows are left out of both: they are the recipe, not the data.

import { cellOf } from './columns';
import { addDays, barOf, clipBar, superGrainOf, ticksOf, toneOf, weekendBands, windowOf } from './timeline';
import { daysBetween } from '../shared/interval';
import { formatDate as fmtDay } from './dueIn';

const INDENT = '    ';

// One exported row: the tree column carries its indentation, everything
// else is the cell the grid drew.
function rowOf(r, cols, ctx) {
  return cols.map((c) => {
    if (c.kind === 'summary') {
      const text = r.summary != null && r.isGroup
        ? r.summary
        : (r.summary || (r.it && r.it.fields && r.it.fields.summary) || '');
      return `${INDENT.repeat(Math.min(r.level, 8))}${text}`;
    }
    const cell = cellOf(c, r, ctx);
    // A formatted figure ("R 1 200.00") goes to the workbook as the number.
    if (c.format && cell.text && cell.num != null && Number.isFinite(cell.num)) return String(Math.round(cell.num * 1e6) / 1e6);
    return cell.text || '';
  });
}

// A number that came out of a cell as text goes back to being a number in
// the workbook — a column of right-aligned text is not a column anyone can
// sum.
const asValue = (s) => {
  if (s === '' || s == null) return '';
  const n = Number(String(s).replace(/,/g, ''));
  return Number.isFinite(n) && /^[-+]?[\d,]*\.?\d+$/.test(String(s).trim()) ? n : s;
};

export function exportRows(rows, cols, ctx) {
  const data = rows.filter((r) => r.kind !== 'rule');
  return {
    head: cols.map((c) => `${c.agg === 'sum' ? 'Σ ' : ''}${c.label}`),
    body: data.map((r) => rowOf(r, cols, ctx)),
    count: data.length,
    // Per row: how deep it sits, and whether it is a heading (a folder or a
    // group) rather than a work item — what the workbook's outline is built from.
    meta: data.map((r) => ({ level: Math.max(0, Number(r.level) || 0), heading: r.kind === 'folder' || !!r.isGroup })),
  };
}

// ---- Excel -------------------------------------------------------------------
export async function toExcel({ rows, cols, ctx, name, filtersText }) {
  // THE STRUCTURE, AS A STRUCTURE — the folders Gantt's workbook and this
  // page's, the best of each. From the folders export: Excel's own OUTLINE
  // (the + / − bars down the left, one level per level of the tree, the
  // heading above what it holds), a bold header row on a dark band that stays
  // frozen while the sheet scrolls, and folders and groups in bold. From this
  // page's: the figures as NUMBERS (not text), the Σ totals on every folder
  // and group and the column widths the reader sees. The header is the first
  // row, as in the folders export — no title lines above it. The tree column
  // is indented by Excel's own indent, not by spaces typed into the name.
  //
  // ExcelJS, as the folders export uses: the SheetJS build this page had
  // cannot write a style or a freeze at all — which is why the header here
  // was plain and never stayed put.
  const ExcelJS = (await import('exceljs')).default;
  const { head, body, count, meta } = exportRows(rows, cols, ctx);
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet(String(name || 'Structure').replace(/[\\/?*[\]:]/g, ' ').slice(0, 31) || 'Structure');
  ws.properties.outlineProperties = { summaryBelow: false, summaryRight: false };
  const HEAD_ROW = 1;
  const hr = ws.addRow(head);
  hr.eachCell((cell) => {
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF333333' } };
    cell.alignment = { vertical: 'middle', wrapText: true };
  });
  const tree = cols.findIndex((c) => c.kind === 'summary');
  body.forEach((cells, i) => {
    const m = meta[i] || { level: 0, heading: false };
    const values = cells.map((v, j) => (j === tree ? String(v || '').slice(INDENT.length * Math.min(m.level, 8)) : asValue(v)));
    const row = ws.addRow(values);
    // Excel's outline stops at seven levels; deeper rows sit at seven.
    row.outlineLevel = Math.min(7, m.level);
    if (tree >= 0) row.getCell(tree + 1).alignment = { indent: Math.min(15, m.level) };
    if (m.heading) row.font = { bold: true };
  });
  cols.forEach((c, i) => { ws.getColumn(i + 1).width = Math.max(10, Math.min(60, Math.round(c.width / 7))); });
  // The heading row is the one worth freezing — a structure is scrolled a
  // long way and a column nobody can name is a column nobody can read.
  ws.views = [{ state: 'frozen', xSplit: 0, ySplit: HEAD_ROW }];
  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${String(name || 'structure').toLowerCase().replace(/[^a-z0-9]+/g, '-')}-${new Date().toISOString().slice(0, 10)}.xlsx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
  return count;
}

// ---- a logo, at the shape it actually is --------------------------------------
//
// It was drawn into a fixed 26 x 9 mm box. A logo whose own proportions are
// not 26:9 — which is most of them — came out STRETCHED, and that is the
// "something looks weird" in every export this page has ever produced.
//
// The image is measured first and fitted INSIDE the box, keeping its
// proportions and sitting against the left edge. The format is read off the
// data URI rather than assumed to be PNG, because a JPEG logo simply failed
// and the export carried on without one.
// The arithmetic on its own — the part that was wrong, and the part that can
// be checked without a browser. CONTAIN, never cover and never stretch: the
// larger of the two ratios is what distorts an image, so the smaller wins.
export function logoFit(w, h, maxW, maxH) {
  if (!(w > 0) || !(h > 0) || !(maxW > 0) || !(maxH > 0)) return null;
  const k = Math.min(maxW / w, maxH / h);
  return { w: w * k, h: h * k };
}

// What jsPDF must be TOLD the image is. It was told 'PNG' whatever arrived,
// so a JPEG logo threw and the export carried on without one.
export function logoType(dataUri) {
  const m = /^data:image\/(png|jpe?g|webp)/i.exec(String(dataUri || ''));
  if (!m) return null;
  const f = m[1].toLowerCase();
  return f === 'png' ? 'PNG' : (f === 'webp' ? 'WEBP' : 'JPEG');
}

async function logoBox(dataUri, maxW, maxH) {
  const type = logoType(dataUri);
  if (!type) return null;
  const size = await new Promise((resolve) => {
    if (typeof Image === 'undefined') { resolve(null); return; }
    const img = new Image();
    img.onload = () => resolve({ w: img.naturalWidth || img.width, h: img.naturalHeight || img.height });
    img.onerror = () => resolve(null);
    img.src = dataUri;
  });
  if (!size) return null;
  const box = logoFit(size.w, size.h, maxW, maxH);
  return box ? { type, ...box } : null;
}

// ---- the chart, laid out for paper -------------------------------------------
// WHAT WAS WRONG WITH THE PRINTED CHART, twice.
//
// First the folder bars ran leftwards across the Status and Due date
// columns: the window was read off the ROWS' own dates, but a folder's bar
// is the span of everything BENEATH it, shut rows included, so it began
// years before the window did and jsPDF drew exactly what it was told. The
// window is now read off the rows WITH their spans, and every bar is
// clipped to the chart besides.
//
// Then the axis: the reader had the screen on Days and the paper came back
// in years. The paper was fitting the WHOLE window — two and a half years —
// into fourteen centimetres, and at that scale a day is a fifteenth of a
// millimetre, so the labels were whatever still fitted. That is a different
// chart from the one on screen, and the reader said so.
//
// THE PAPER SAYS WHAT THE SCREEN SAYS. The chart is drawn at the grain the
// reader chose, at a scale where that grain's own label is legible — a day
// column wide enough for "28", a week for "CW39", a month for "Sep 26". If
// the whole plan fits at that scale, the whole plan is printed; if it does
// not, the paper shows the same slice the screen shows, from the same
// left-hand edge, and the header says so. A reader who wants the whole of a
// three-year plan on one page picks Months, exactly as they would on screen.
//
// Millimetres per DAY at each grain. Each answers "what does this grain's
// own label need": a day column 2.6mm for two digits at 5.5pt, a week 7mm
// for "CW39", a month 10mm for "Sep 26".
export const MM_PER_DAY = { day: 2.6, week: 1.0, month: 0.33 };

export function chartLayout({ rows, spanOfRow = null, chartW, grain = 'day', view = null, now = new Date() }) {
  const dated = (rows || []).map((r) => {
    if (!r || r.kind === 'rule' || r.it || typeof spanOfRow !== 'function') return r;
    const span = spanOfRow(r);
    return span ? { ...r, ...span } : r;
  });
  const whole = windowOf(dated, { now });
  const g = MM_PER_DAY[grain] ? grain : 'day';
  const perDay = MM_PER_DAY[g];
  const fits = whole.days * perDay <= chartW;
  const days = fits ? whole.days : Math.max(1, Math.floor(chartW / perDay));
  // Not the whole plan: the same left-hand edge as the screen, kept inside
  // the plan's own window so the paper never shows a stretch with nothing
  // on it when there was something to show.
  let from = whole.from;
  if (!fits) {
    const want = view && view.from ? String(view.from).slice(0, 10) : whole.from;
    const latest = addDays(whole.to, -(days - 1));
    from = want < whole.from ? whole.from : (want > latest ? latest : want);
  }
  const to = addDays(from, days - 1);
  const win = { from, to, today: whole.today, days, spanDays: whole.spanDays };
  return {
    dated, win, perDay, grain: g, whole: fits,
    ticks: ticksOf(win, g, perDay),
    supers: ticksOf(win, superGrainOf(g), perDay),
    bands: weekendBands(win, g, perDay),
  };
}

// ---- the columns, sized to what they hold ------------------------------------
// The table used to take the screen's column widths as proportions of the
// paper. On screen those widths are a starting point the reader can drag; on
// paper nothing can be dragged, and the result was "Due In - Zebra…",
// "ZAR Value (Ex…" and a Count column as wide as a date. The paper's columns
// are sized to their CONTENT: the heading's longest word, and the widest
// cell in a sample of the rows, each capped so one long summary cannot take
// the page. Room to spare goes to the tree column first, then to everyone by
// share; too little room is taken from everyone by share, down to the
// heading's own floor.
//
// `measure(text)` is jsPDF's ruler at the body size, handed in so this
// stays arithmetic and runs in the suite.
export function pdfColumnWidths(columns, tableW, measure, { pad = 3, sample = 400 } = {}) {
  const list = (columns || []).map((c) => {
    const headWord = String(c.head || '').split(/\s+/).reduce((a, w) => Math.max(a, measure(w)), 0);
    const cells = c.cells || [];
    const step = cells.length > sample ? Math.ceil(cells.length / sample) : 1;
    let widest = 0;
    for (let i = 0; i < cells.length; i += step) {
      const w = measure(String(cells[i] == null ? '' : cells[i]));
      if (w > widest) widest = w;
    }
    const cap = Number(c.cap) > 0 ? Number(c.cap) : 45;
    const min = Math.max(8, headWord + pad);
    const need = Math.max(min, Math.min(cap, Math.max(widest, headWord) + pad));
    return { id: c.id, min, need, tree: !!c.tree, uncapped: Math.max(widest, headWord) + pad };
  });
  if (!list.length || !(tableW > 0)) return list.map((c) => ({ id: c.id, width: c.need }));
  const total = list.reduce((a, c) => a + c.need, 0);
  if (total <= tableW) {
    let slack = tableW - total;
    const widths = list.map((c) => c.need);
    // The tree column first, up to what its longest row actually wanted.
    const t = list.findIndex((c) => c.tree);
    if (t >= 0 && list[t].uncapped > list[t].need) {
      const give = Math.min(slack, list[t].uncapped - list[t].need);
      widths[t] += give;
      slack -= give;
    }
    const base = widths.reduce((a, w) => a + w, 0);
    return list.map((c, i) => ({ id: c.id, width: widths[i] + (slack * widths[i]) / base }));
  }
  const mins = list.reduce((a, c) => a + c.min, 0);
  const free = total - mins;
  const room = Math.max(0, tableW - mins);
  const k = free > 0 ? room / free : 0;
  return list.map((c) => ({ id: c.id, width: c.min + (c.need - c.min) * k }));
}

// jsPDF's standard fonts have no Σ, and drew it as £ with the letters that
// followed spaced out. The paper says it in words.
export const paperHeading = (label) => String(label || '').replace(/^Σ\s*/, 'Sum of ');

// ---- PDF ---------------------------------------------------------------------
// A real table, drawn and paginated, not a picture of a scrolled grid.
// Headings wrap to two lines rather than ending in an ellipsis; folder and
// group rows are shaded; every other row is tinted so the eye can follow
// one across thirteen columns.
//
// AND THE TIMELINE COMES WITH IT. A reader who exports the page with the
// chart on wants the chart: a structure printed without its bars is a list of
// dates, which is the thing the Gantt exists to stop people reading. It is
// drawn at the interval the reader chose — see chartLayout for what that
// means on paper.
export async function toPdf({
  rows, cols, ctx, name, pageName, filtersText, logoDataUri,
  timeline = false, grain = 'day', spanOfRow = null, chartView = null,
}) {
  const { jsPDF } = await import('jspdf');
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4', compress: true });
  const PW = 297; const PH = 210; const M = 8;
  const W = PW - 2 * M;
  const HEAD = 16; const FOOT = 7;
  const LH = 4.6;          // a body row
  const HH = 8.2;          // the heading band — two lines of heading
  const BODY = 7.2; const HEADPT = 6.4;

  const { head, body, count } = exportRows(rows, cols, ctx);

  const CHART = timeline ? Math.min(150, Math.max(80, W * 0.45)) : 0;
  const TABLE = W - CHART;
  const layout = timeline ? chartLayout({ rows, spanOfRow, chartW: CHART, grain, view: chartView }) : null;
  const win = layout ? layout.win : null;
  const perDay = layout ? layout.perDay : 0;
  const datedRows = layout ? layout.dated : rows;

  // The columns, sized to what they hold — measured with the ruler that will
  // draw them.
  doc.setFontSize(BODY);
  const measure = (t) => doc.getTextWidth(String(t == null ? '' : t));
  const cw = pdfColumnWidths(cols.map((c, i) => ({
    id: c.id, head: paperHeading(head[i]), cells: body.map((r) => r[i]),
    tree: c.kind === 'summary', cap: c.kind === 'summary' ? 95 : 42,
  })), TABLE, measure).map((c) => c.width);

  const fit = (text, w, size) => {
    doc.setFontSize(size);
    let t = String(text == null ? '' : text);
    if (doc.getTextWidth(t) <= w - 2) return t;
    while (t.length > 1 && doc.getTextWidth(`${t}…`) > w - 2) t = t.slice(0, -1);
    return `${t}…`;
  };
  // A heading wraps to two lines; what still will not fit is cut on the
  // second line, not the first.
  const headLines = (text, w) => {
    doc.setFontSize(HEADPT);
    const lines = doc.splitTextToSize(String(text || ''), Math.max(4, w - 2));
    if (lines.length <= 2) return lines;
    return [lines[0], fit(lines.slice(1).join(' '), w, HEADPT)];
  };

  const mark = logoDataUri ? await logoBox(logoDataUri, 26, 9) : null;
  const textLeft = mark ? M + mark.w + 4 : M;
  const GRAIN_WORD = { day: 'days', week: 'weeks', month: 'months' };

  let page = 0;
  const header = () => {
    page += 1;
    doc.setFillColor(248, 248, 250);
    doc.rect(0, 0, PW, HEAD, 'F');
    if (mark) {
      try { doc.addImage(logoDataUri, mark.type, M, 3 + (9 - mark.h) / 2, mark.w, mark.h, undefined, 'FAST'); }
      catch { /* a logo that will not draw is not worth failing the export for */ }
    }
    doc.setFontSize(11); doc.setTextColor(20);
    doc.text(String(pageName || name || 'Structure'), textLeft, 8);
    doc.setFontSize(7.4); doc.setTextColor(110);
    const range = layout
      ? `  ·  timeline ${fmtDay(win.from)} – ${fmtDay(win.to)} in ${GRAIN_WORD[layout.grain] || layout.grain}${layout.whole ? '' : ' — the part on screen; pick Months for the whole plan'}`
      : '';
    doc.text(fit(`${filtersText || ''}${range}`, W - 70 - (textLeft - M), 7.4), textLeft, 12.5);
    doc.text(`${count.toLocaleString()} rows · ${new Date().toLocaleString()}`, PW - M, 8, { align: 'right' });

    // ---- the column headings, two lines each ----------------------------------
    doc.setFillColor(236, 237, 241);
    doc.rect(M, HEAD, W, HH, 'F');
    doc.setTextColor(60);
    let x = M;
    cols.forEach((c, i) => {
      const right = c.align === 'right';
      const lines = headLines(paperHeading(head[i]), cw[i]);
      // Bottom-aligned, so a one-line heading sits on the same baseline as
      // the second line of a two-line one.
      const y0 = HEAD + HH - 1.6 - (lines.length - 1) * 2.6;
      doc.setFontSize(HEADPT);
      lines.forEach((ln, li) => doc.text(ln, right ? x + cw[i] - 1 : x + 1, y0 + li * 2.6, { align: right ? 'right' : 'left' }));
      x += cw[i];
      if (i < cols.length - 1) { doc.setDrawColor(222); doc.line(x, HEAD, x, HEAD + HH); }
    });

    // ---- the chart's axis, two tiers like the screen's ------------------------
    if (layout) {
      const x0 = M + TABLE;
      doc.setFillColor(236, 237, 241);
      doc.rect(x0, HEAD, CHART, HH, 'F');
      const T1 = HH / 2;
      layout.supers.forEach((t) => {
        doc.setDrawColor(200);
        doc.line(x0 + t.x, HEAD, x0 + t.x, HEAD + T1);
        doc.setFontSize(6.2); doc.setTextColor(40);
        const label = String(t.label || '');
        if (doc.getTextWidth(label) + 1.5 <= t.w) doc.text(label, x0 + t.x + t.w / 2, HEAD + T1 - 1.2, { align: 'center' });
      });
      doc.setDrawColor(210);
      doc.line(x0, HEAD + T1, x0 + CHART, HEAD + T1);
      layout.ticks.forEach((t) => {
        if (t.weekend) { doc.setFillColor(226, 227, 232); doc.rect(x0 + t.x, HEAD + T1, t.w, HH - T1, 'F'); }
        doc.setDrawColor(210);
        doc.line(x0 + t.x, HEAD + T1, x0 + t.x, HEAD + HH);
        doc.setFontSize(5.5); doc.setTextColor(t.weekend ? 150 : 70);
        const label = String(t.label || '');
        const short = label.split(' ')[0];
        const pick = doc.getTextWidth(label) + 1 <= t.w ? label : (doc.getTextWidth(short) + 1 <= t.w ? short : '');
        if (pick) doc.text(pick, x0 + t.x + t.w / 2, HEAD + HH - 1.6, { align: 'center' });
      });
      // Weekends and gridlines, behind every row of the page.
      layout.bands.forEach((b) => { doc.setFillColor(243, 243, 246); doc.rect(x0 + b.x, HEAD + HH, b.w, PH - FOOT - HEAD - HH, 'F'); });
      layout.supers.forEach((t) => { doc.setDrawColor(215); doc.line(x0 + t.x, HEAD + HH, x0 + t.x, PH - FOOT); });
      // TODAY, the one line on this chart worth finding at a glance.
      const todayX = x0 + (daysBetween(win.from, win.today) || 0) * perDay;
      if (todayX >= x0 && todayX <= x0 + CHART) {
        doc.setDrawColor(245, 166, 35);
        doc.setLineWidth(0.4);
        doc.line(todayX, HEAD, todayX, PH - FOOT);
        doc.setLineWidth(0.2);
      }
      doc.setDrawColor(190);
      doc.line(x0, HEAD, x0, PH - FOOT);
    }
    doc.setDrawColor(200);
    doc.line(M, HEAD + HH, M + W, HEAD + HH);
    return HEAD + HH;
  };

  let y = header();
  const bottom = PH - FOOT;
  const data = datedRows.filter((r) => r.kind !== 'rule');

  body.forEach((cells, ri) => {
    if (y + LH > bottom) {
      doc.setFontSize(7); doc.setTextColor(140);
      doc.text(`Page ${page}`, PW - M, PH - 3, { align: 'right' });
      doc.addPage();
      y = header();
    }
    const r = data[ri];
    if (r.isFolder) { doc.setFillColor(232, 234, 240); doc.rect(M, y, TABLE, LH, 'F'); }
    else if (r.isGroup) { doc.setFillColor(241, 242, 246); doc.rect(M, y, TABLE, LH, 'F'); }
    else if (ri % 2 === 1) { doc.setFillColor(250, 250, 252); doc.rect(M, y, TABLE, LH, 'F'); }
    let x = M;
    doc.setFontSize(BODY);
    doc.setTextColor(r.isGroup ? 20 : 45);
    if (r.isFolder) doc.setFont(undefined, 'bold');
    cols.forEach((c, i) => {
      const right = c.align === 'right';
      doc.text(fit(cells[i], cw[i], BODY), right ? x + cw[i] - 1 : x + 1, y + LH - 1.4, { align: right ? 'right' : 'left' });
      x += cw[i];
    });
    if (r.isFolder) doc.setFont(undefined, 'normal');
    // THE BAR, BESIDE ITS OWN ROW. Same rules as the screen: blue on the
    // plan, red past its due date, green done, a diamond for a row with one
    // date only — and clipped to the chart, whatever the window.
    if (layout) {
      const bar = clipBar(barOf(r, win, perDay), CHART);
      if (bar) {
        const tone = toneOf(r, win.today);
        if (tone === 'late') doc.setFillColor(220, 38, 38);
        else if (tone === 'done') doc.setFillColor(22, 163, 74);
        else doc.setFillColor(74, 144, 217);
        const bx = M + TABLE + bar.x;
        const h = r.isGroup ? LH - 3 : LH - 2.2;
        if (bar.kind === 'bar') doc.rect(bx, y + (LH - h) / 2, Math.max(0.6, bar.w), h, 'F');
        else doc.circle(bx, y + LH / 2, 0.9, 'F');
      }
    }
    doc.setDrawColor(228);
    doc.line(M, y + LH, M + W, y + LH);
    y += LH;
  });

  doc.setFontSize(7); doc.setTextColor(140);
  doc.text(`Page ${page}`, PW - M, PH - 3, { align: 'right' });
  doc.save(`${String(name || 'structure').toLowerCase().replace(/[^a-z0-9]+/g, '-')}-${new Date().toISOString().slice(0, 10)}.pdf`);
  return count;
}
